import chunk from 'lodash/chunk'

import { channelsInstance, instance } from '@/api/instances'

import { buildMemberNameProps } from './utils'

const searchByQuery = async (store, { query, signal }) => {
  try {
    return await instance.get(`search_persons`, {
      signal,
      params: { query }
    })
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'member/searchByQuery' },
      { root: true }
    )
  }
}

/**
 * Get members by their channelPersonId
 * Params:
 * - channelPersonIds(array of strings)
 * - notifications(boolean) - trow the error message or not
 * - leanResponse(boolean) - get all the members from array or the rest based on members on the store
 * @param {Store object} store
 * @param {Object} params
 * @returns void
 */
const loadByChannelPersonIds = async (
  store,
  { channelPersonIds, notifications = true }
) => {
  try {
    let ids = channelPersonIds
    ids = channelPersonIds.filter(
      id =>
        !Object.keys(store.state.all).find(
          key => store.state.all[key].channelPersonId === id
        )
    )

    ids = ids.filter(Boolean)

    if (!ids.length) return

    const chunks = chunk(ids, 20)

    for (const page of chunks) {
      const ids = page.join(',')

      const members = await channelsInstance.get(
        `persons?channelPersonIds=${ids}`
      )

      store.commit('setMembers', members)
    }
  } catch (error) {
    if (notifications) {
      store.commit(
        'errors/setError',
        'Opa, não conseguimos pegar os dados dos membros',
        { root: true }
      )
    }
  }
}

/**
 * Get the member by id if already have this member on store, get the member from store
 * otherwise call api to get member and save in store
 * @param {Store object} store
 * @param {Object} params
 * @returns Member object
 */
const getMember = async (store, { personId, notifications = true }) => {
  if (!personId) {
    return null
  }
  try {
    const stateMember = store.state.all[personId]

    if (stateMember !== undefined) {
      return stateMember
    }

    const memberData = await instance.get(`persons/${personId}`)
    const member = { ...memberData, ...buildMemberNameProps(memberData) }
    store.commit('setMember', member)
    return member
  } catch (error) {
    if (notifications) {
      store.commit(
        'errors/setError',
        'Opa, não conseguimos pegar os dados do membro',
        { root: true }
      )
    }
  }
}

const loadMembersPhotos = (store, shouldLoadPhotos) => {
  if (!shouldLoadPhotos) return

  const members = Object.values(store.state.all)
    .filter(person => person.photo && !person.photoURL)
    .map(person => ({
      id: person.id,
      channelPersonId: person.channelPersonId,
      fileId: person.photo.id
    }))

  if (members.length) {
    const ids = members.map(item => item.fileId)
    const chunks = chunk(ids, 20)

    chunks.forEach(async fileIds => {
      const responseData = await store.dispatch(
        'file/getFileInBatch',
        fileIds,
        { root: true }
      )

      members.forEach(person => {
        store.commit('updateMemberPhoto', {
          id: person.id,
          photoURL: responseData.find(item => item.id === person.fileId)?.url
        })
      })
    })
  }
}

const getPersonById = async (_, personId) => {
  if (!personId) return
  return await instance.get(`persons/${personId}`)
}

const searchMemberForHCSpecialist = async (_, searchParam) => {
  if (!searchParam) return null
  try {
    const response = await instance.get(
      `v2/cockpit_persons?query=${searchParam}`
    )

    return Object.keys(response).length === 0 ? null : response
  } catch (error) {
    return null
  }
}

const getDischargeSummary = async (store, { personId, summaryId }) => {
  try {
    const config = { responseType: 'blob' }
    return await instance.get(
      `persons/${personId}/discharge_summary/${summaryId}`,
      config
    )
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'member/getDischargeSummary' },
      { root: true }
    )
  }
}

export default {
  searchByQuery,
  loadByChannelPersonIds,
  getMember,
  loadMembersPhotos,
  getPersonById,
  searchMemberForHCSpecialist,
  getDischargeSummary
}
